import { BufferGeometry, Color, Matrix4, Vector3 } from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { GeometryKit, houseGeometry, treeGeometry } from '../forge/environment.js'
import { CELL, GROUND, SCENERY, World } from '../sim/world.js'
import { buildSurfaceHeights, hash2, surfaceHeightAt } from './terrain.js'

export interface ScatterGeometryData {
  positions: Float32Array
  normals: Float32Array
  colors: Float32Array
  indices: Uint32Array
}

function utilityGeometry(kind: number): BufferGeometry {
  const kit = new GeometryKit()
  kit.box(0, 0, 0, 0.96, 0.08, 0.96, '#949f98')
  if (kind === SCENERY.Tank) {
    kit.cylinder(0, 0.08, 0, 0.36, 1.38, '#c2cac0')
    kit.cylinder(0, 0.38, 0, 0.38, 0.12, '#6e9294')
    kit.cylinder(0, 1.12, 0, 0.38, 0.12, '#6e9294')
    kit.box(0.15, 1.46, 0, 0.13, 0.2, 0.13, '#606e6e')
    for (let step = 0; step < 6; step++) kit.box(0, 0.15 + step * 0.21, 0.4, 0.26, 0.04, 0.05, '#747f7b')
  } else {
    kit.box(0, 0.08, 0, 0.84, 0.74, 0.9, '#b29560')
    kit.box(0, 0.82, 0, 0.84, 0.04, 0.9, '#c8ae77')
    for (let rib = 0; rib < 5; rib++) {
      for (const side of [-1, 1]) kit.box(-0.34 + rib * 0.17, 0.12, side * 0.46, 0.045, 0.66, 0.04, '#8c7954')
    }
  }
  return kit.finish()
}

/** One closed, shadow-casting batch. Solid props come only from simulation footprints. */
export function buildScatterGeometry(world: World): ScatterGeometryData {
  const kit = new GeometryKit()
  const pieces: BufferGeometry[] = []
  const surface = buildSurfaceHeights(world), size = world.size
  const templates = new Map<number, BufferGeometry>()
  const road = (x: number, y: number): boolean =>
    world.inBounds(x, y) && world.ground[y * size + x] === GROUND.Road

  for (let cy = 0; cy < size; cy++) for (let cx = 0; cx < size; cx++) {
    const cell = cy * size + cx, x = cx + 0.5, z = cy + 0.5, y = surface[cell]
    const ground = world.ground[cell], kind = world.scenery[cell]
    if (ground === GROUND.Road || ground === GROUND.Parking) {
      kit.box(x, y - 0.025, z, 1, 0.04, 1, ground === GROUND.Road ? '#596465' : '#77817d')
      if (ground === GROUND.Road) {
        for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          if (!road(cx + dx, cy + dz)) {
            kit.box(x + dx * 0.465, y + 0.016, z + dz * 0.465, dx ? 0.07 : 1, 0.018, dz ? 0.07 : 1, '#a7afa2')
          }
        }
        const vertical = road(cx - 1, cy) && road(cx + 1, cy) && !road(cx - 2, cy) && !road(cx + 2, cy)
        const horizontal = road(cx, cy - 1) && road(cx, cy + 1) && !road(cx, cy - 2) && !road(cx, cy + 2)
        if (vertical && cy % 2 === 0) kit.box(x, y + 0.016, z, 0.055, 0.008, 0.62, '#d9d8bd')
        if (horizontal && cx % 2 === 0) kit.box(x, y + 0.016, z, 0.62, 0.008, 0.055, '#d9d8bd')
      } else {
        kit.box(x - 0.46, y + 0.016, z, 0.035, 0.008, 1, '#d4d7c7')
        if (world.ground[(cy - 1) * size + cx] !== GROUND.Parking
          || world.ground[(cy + 1) * size + cx] !== GROUND.Parking) {
          kit.box(x, y + 0.016, z, 0.93, 0.008, 0.045, '#d4d7c7')
        }
      }
    }
    if (kind !== SCENERY.None && (world.flags[cell] & CELL.Scenery) !== 0) {
      const variant = Math.floor(hash2(cx, cy) * 3)
      const key = kind * 10 + (kind === SCENERY.Tree ? variant : 0)
      let geometry = templates.get(key)
      if (!geometry) {
        geometry = kind === SCENERY.Tree ? treeGeometry(variant)
          : kind === SCENERY.House ? houseGeometry(0)
            : kind === SCENERY.Warehouse ? houseGeometry(2) : utilityGeometry(kind)
        templates.set(key, geometry)
      }
      const rotation = kind === SCENERY.Tree ? variant * Math.PI / 2 : cy < size / 2 ? -Math.PI / 2 : Math.PI / 2
      const matrix = new Matrix4().makeRotationY(rotation).setPosition(x, y, z)
      if (kind === SCENERY.Tree) matrix.scale(new Vector3(1, 0.78 + hash2(cx + 13, cy - 17) * 0.3, 1))
      pieces.push(geometry.clone().applyMatrix4(matrix))
    } else if (ground === GROUND.Natural && (world.flags[cell] & (CELL.Field | CELL.Cliff | CELL.Water | CELL.Scenery)) === 0
      && hash2(cx * 7, cy * 11) < 0.13) {
      // Low grass is passable; unlike trees it never hides a unit or implies a wall.
      const height = surfaceHeightAt(world, surface, x, z)
      for (let blade = 0; blade < 3; blade++) {
        kit.box(x - 0.15 + blade * 0.14, height, z, 0.055, 0.07 + blade * 0.025, 0.07, '#7c985d')
      }
    }
  }
  pieces.push(kit.finish())
  const geometry = mergeGeometries(pieces, false)!
  const colors = geometry.getAttribute('color').array as Float32Array
  const color = new Color()
  // The game composites palette values in display RGB; studies use linear Three colors.
  for (let i = 0; i < colors.length; i += 3) {
    color.setRGB(colors[i], colors[i + 1], colors[i + 2]).convertLinearToSRGB()
    colors[i] = color.r; colors[i + 1] = color.g; colors[i + 2] = color.b
  }
  const positions = geometry.getAttribute('position').array as Float32Array
  const data = {
    positions, colors,
    normals: geometry.getAttribute('normal').array as Float32Array,
    indices: Uint32Array.from({ length: positions.length / 3 }, (_, i) => i),
  }
  for (const piece of pieces) piece.dispose()
  for (const template of templates.values()) template.dispose()
  geometry.dispose()
  return data
}
